import React from 'react';
import { PrintService } from '../../services/print-service';
import { Button } from '../ui/button';
import { Printer, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface PrintToPDFProps {
  surveyId: string;
  buttonText?: string;
  buttonVariant?: 'default' | 'outline' | 'secondary' | 'ghost';
  buttonSize?: 'default' | 'sm' | 'lg';
  showIcon?: boolean;
  className?: string;
  onPrintStart?: () => void;
  onPrintComplete?: () => void;
  onPrintError?: (error: Error) => void;
  highlightedFields?: Record<string, boolean>;
}

export const PrintToPDF: React.FC<PrintToPDFProps> = ({
  surveyId,
  buttonText = 'Print PDF',
  buttonVariant = 'default',
  buttonSize = 'default',
  showIcon = true,
  className = '',
  onPrintStart,
  onPrintComplete,
  onPrintError,
  highlightedFields,
}) => {
  const [isPrinting, setIsPrinting] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [progressMessage, setProgressMessage] = React.useState('');
  const { toast } = useToast();
  const toastRef = React.useRef<{
    id: string;
    dismiss: () => void;
    update: (props: any) => void;
  } | null>(null);

  const handlePrint = async () => {
    try {
      setIsPrinting(true);
      setProgress(0);
      onPrintStart?.();

      // Show initial toast with progress
      toastRef.current = toast({
        title: 'Generating PDF',
        description: (
          <div className="space-y-2">
            <div className="text-sm">{progressMessage || 'Starting...'}</div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ),
        duration: 60000, // Keep open for 1 minute
      });

      // Progress callback
      const handleProgress = (newProgress: number, message: string) => {
        setProgress(newProgress);
        setProgressMessage(message);

        // Update existing toast
        if (toastRef.current) {
          toastRef.current.update({
            id: toastRef.current.id,
            title: 'Generating PDF',
            description: (
              <div className="space-y-2">
                <div className="text-sm">{message}</div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${newProgress}%` }}
                  />
                </div>
              </div>
            ),
            duration: 60000,
          });
        }
      };

      await PrintService.printSurvey(surveyId, handleProgress, { highlightedFields });

      // Dismiss progress toast and show success
      if (toastRef.current) {
        toastRef.current.dismiss();
        toast({
          title: 'PDF Ready',
          description: 'Your PDF has been prepared for printing',
          duration: 2000,
        });
      }

      onPrintComplete?.();
    } catch (error) {
      console.error('Print failed:', error);

      // Show error toast
      toast({
        title: 'PDF Generation Failed',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive',
        duration: 5000,
      });

      onPrintError?.(error as Error);
    } finally {
      setIsPrinting(false);
      setProgress(0);
      setProgressMessage('');
      toastRef.current = null;
    }
  };

  return (
    <Button
      onClick={handlePrint}
      disabled={isPrinting}
      variant={buttonVariant}
      size={buttonSize}
      className={className}
    >
      {showIcon &&
        (isPrinting ? (
          <Download className="w-4 h-4 mr-2 animate-pulse" />
        ) : (
          <Printer className="w-4 h-4 mr-2" />
        ))}
      {isPrinting ? `Generating... ${progress > 0 ? `${progress}%` : ''}` : buttonText}
    </Button>
  );
};

interface QuickPrintButtonProps {
  surveyId: string;
  className?: string;
}

export const QuickPrintButton: React.FC<QuickPrintButtonProps> = ({ surveyId, className }) => {
  return (
    <PrintToPDF
      surveyId={surveyId}
      buttonText="Print"
      buttonVariant="outline"
      buttonSize="sm"
      className={className}
    />
  );
};
